'use client';

import React from 'react';

import { Button } from '@wwe-portal/ui/components/button';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';

import { useTranslations } from 'next-intl';

import Image from 'next/image';
import { TriviaGoHome } from './trivia-go-home';
import {
  useGameStatus,
  useTriviaStore,
} from '@wwe-portal/web/providers/stores/trivia-store';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import Link from 'next/link';
import { WEBCOIN_SECTIONS } from '@wwe-portal/web/utils/constants/webcoin';
import { CATEGORY_IDS } from '@wwe-portal/web/utils/constants/offer';
import { DialogCloseButton } from '../../../../../packages/ui/components/dialog';
import {
  usePlayAgainMutation,
  useTriviaContinueMutation,
} from '@wwe-portal/web/providers/queries/trivia-rumble.mutation';

export const TriviaGameFailed = () => {
  const t = useTranslations();
  const gameStatus = useGameStatus();
  const closeGame = useTriviaStore((state) => state.closeGame);
  const gameData = useTriviaStore((state) => state.gameData);
  const gameConfig = useTriviaStore((state) => state.gameConfig);
  if (gameData == null || gameConfig == null) return null;
  const retryCost = gameData.retryCost;
  const isLastQuestion =
    gameData.questionIndex >= gameConfig.numberOfQuestions - 1;
  const canContinueAnswer = retryCost != null && !isLastQuestion;
  const canPlayAgain = !canContinueAnswer;

  if (gameStatus !== 'lost') return null;
  return (
    <div className="fade-in absolute inset-0 flex items-center justify-center">
      <div className="bg-borderColor3 relative mx-4 h-[380px] w-[400px] rounded-xl">
        <Image
          fill
          alt=""
          className="absolute inset-0 overflow-hidden rounded-xl object-fill"
          src="/assets/images/trivia-rumble/trivia-popup-background.webp"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-10">
          <span className="mb-[10px] rounded-full border border-[#4f5794] p-[5px]">
            <Image
              src="/assets/icons/trivia-bad-icon.svg"
              alt="trivia-rumble-icon"
              width={50}
              height={50}
            />
          </span>
          <p className="mb-[30px] text-2xl font-semibold">{t('too_bad')}</p>
          <p className="text-textColor2 text-base font-medium">
            {canContinueAnswer ? t('want_to_continue') : t('want_to_try_again')}
          </p>
          {canContinueAnswer ? (
            <span
              className="rounded-6 mt-5 flex h-9 w-16 items-center justify-center text-xl"
              style={{
                backgroundColor: 'rgba(179, 186, 233, 0.05)',
                border: '1px solid var(--borderColor17)',
              }}
            >
              {gameData.retryCountdown}
            </span>
          ) : null}
          {canContinueAnswer && <ContinueQuestionActions />}
          {canPlayAgain && <PlayAgainQuestionActions />}
        </div>
        <DialogCloseButton onClose={closeGame} />
      </div>
    </div>
  );
};

const ContinueQuestionActions = () => {
  const t = useTranslations();

  const gameData = useTriviaStore((state) => state.gameData);
  const gameConfig = useTriviaStore((state) => state.gameConfig);
  const gameId = useTriviaStore((state) => state.gameId);
  const currentUser = useGetCurrentUser();

  const { mutate: continueAnswerMutate, isPending } =
    useTriviaContinueMutation();

  if (gameData == null || gameConfig == null || gameId == null) return null;
  const neededWebCoin = gameData.retryCost;
  const enoughWebcoin =
    neededWebCoin != null &&
    currentUser?.webCoin != null &&
    currentUser.webCoin >= neededWebCoin;

  return (
    <>
      {enoughWebcoin && (
        <Button
          variant="blue"
          onClick={() => {
            continueAnswerMutate();
          }}
          size="large"
          className="my-[30px] px-14"
          data-id="continue_game_button"
          loading={isPending}
          loadingText={t('processing')}
        >
          <div className="flex items-center gap-2.5">
            <IcoMoonIcon iconName="web-coin-circle" className="size-5" />
            <span className="text-xl">{neededWebCoin}</span>
          </div>
        </Button>
      )}

      {!enoughWebcoin && (
        <Link href={`/special-offers?category=${CATEGORY_IDS.WEBCOINS}`}>
          <Button
            variant="blue"
            size="large"
            className="my-[30px] px-14"
            data-id={`add_webcoin_button`}
          >
            <div className="flex items-center gap-2.5">
              <IcoMoonIcon iconName="web-coin-circle" className="size-5" />
              <span className={'mb-0 text-sm'}>{t('get_more_web_coins')}</span>
            </div>
          </Button>
        </Link>
      )}
    </>
  );
};

const PlayAgainQuestionActions = () => {
  const t = useTranslations();
  const gameConfig = useTriviaStore((state) => state.gameConfig);
  const energyCost = gameConfig?.energyCost;
  const currentUser = useGetCurrentUser();
  const { mutate: playAgainMutate, isPending } = usePlayAgainMutation();
  const energy = currentUser?.energy;
  const enoughEnergy =
    energy != null && energyCost != null && energy >= energyCost;

  return (
    <>
      {!enoughEnergy && (
        <Link href={`/web-coin-shop?category=${WEBCOIN_SECTIONS.ENERGY_PACKS}`}>
          <Button
            variant="blue"
            size="large"
            className="mb-2 mt-[30px] px-14"
            data-id={`add_energy_button`}
          >
            <div className="flex items-center">
              <IcoMoonIcon iconName="battery-low" className="size-5" />
              <span className="uppercase">{t('get_more_energy')}</span>
            </div>
          </Button>
        </Link>
      )}

      {enoughEnergy && (
        <Button
          variant="blue"
          size="large"
          onClick={() => {
            playAgainMutate();
          }}
          className="mb-2 mt-[30px] px-14"
          data-id={`play_again_button`}
          loading={isPending}
          loadingText={t('processing')}
        >
          <span className="uppercase">{t('play_again')}</span>
        </Button>
      )}
      <div className="text-chetwodeBlue mb-2 flex items-center justify-center text-center">
        <IcoMoonIcon
          iconName="battery-charging"
          className="text-orange mr-[5px] align-middle text-sm"
          style={{ width: 16, height: 16 }}
        />
        {t('cost')} : {energyCost || ''} {t('energy')}
      </div>
      <TriviaGoHome />
    </>
  );
};
